import BasicButton from 'components/BasicButton';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicLabel from 'components/BasicLabel';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicTextArea from 'components/BasicTextArea';
import CheckboxOrRadioGroup from 'components/CheckboxOrRadioGroup';
import LazyLoadSelect from 'components/LazyLoadSelect';

import CreatableLazySelect from 'components/LazyLoadSelect/lazy-load-creatable';
import {ANIMATION_TIMEOUT_APPEAR} from 'constants/animation';
import {ENDPOINT} from 'constants/endpoints';
import {ErrorMessage, Formik} from 'formik';
import {useApi} from 'hooks/useApi';
import React from 'react';
import {CSSTransition} from 'react-transition-group';

import {ShortlistAddCandidatesFormValidationSchema} from 'scenes/Form/components/ShortlistAddCandidatesForm/validate';
import ProgressBar from '../../../../components/ProgressBar';

const ShortListTypeOptions = [
  { label: 'My Shortlist', value: '1' },
  { label: 'Shared With Me', value: '0' },
];

const ShortlistAddCandidatesForm = props => {
  const {
    params: { ids, showActivityAfterSave = false },
    actions: { proceed, dismiss, handleError },
  } = props;

  const api = useApi();
  const [progress, setProgress] = React.useState(0);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    // split ids into chunks of 500
    const chunkedIds = ids.reduce((acc, id, index) => {
      const chunkIndex = Math.floor(index / 500);

      if (!acc[chunkIndex]) {
        acc[chunkIndex] = [];
      }

      acc[chunkIndex].push(id);

      return acc;
    }, []);

    const isOwner = values.shortlistType === '1';
    const {
      Name: { value, label, __isNew__ = false },
      Description = '',
    } = values;

    const params = isOwner
      ? {
        Description,
      }
      : {
      };

    __isNew__ ? (params.Name = label) : (params.id = value);
    showActivityAfterSave
      ? (params.showActivity = 1)
      : (params.showActivity = 0);

    const endpoint = isOwner
      ? ENDPOINT.ACTION.SHORTLIST.SAVE
      : ENDPOINT.ACTION.SHORTLIST.SHARED_SAVE;

    // send each chunk to the server synchronously
    const results = [];

    for (const chunk of chunkedIds) {
      params.candidates = chunk;
      try {
        results.push(await new Promise((resolve, reject) => {
          api.post(endpoint, params, {
            handleError: response => {
              setSubmitting(false);
              reject(response);
            },
            handleSuccess: ({ id, activity }) => {
              params.id = id;
              setProgress(progress => progress + chunk.length);
              resolve({id: params.id ? id : params.id, activity});
            },
          });
        }));
      } catch (error) {
        handleError(error, setErrors);
        break;
      }
    }

    setSubmitting(false);

    if (results.length > 0) {
      proceed(results[results.length - 1]);
    }
  };

  const onChangeShortlist = (name, value, { setFieldValue }) => {
    if (value && value.desc) {
      setFieldValue('Description', value.desc);
    } else {
      setFieldValue('Description', '');
    }

    setFieldValue(name, value);
  };

  const onShortListTypeChange = (e, { handleChange, setFieldValue }) => {
    onChangeShortlist('Name', null, { setFieldValue });
    handleChange(e);
  };

  const getShortlistOptions = (query, offset) =>
    new Promise(resolve => {
      api.get(
        ENDPOINT.LIST.SHORTLIST,
        {
          query,
          offset,
        },
        {
          handleSuccess: response => {
            resolve(response);
          },
        }
      );
    });

  const getSharedShortlistOptions = (query, offset) =>
    new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.LIST.SHORTLIST,
        { shared: 'Yes', query, offset },
        {
          handleError: response => reject(response),
          handleSuccess: data => resolve(data),
        }
      );
    });

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={{
            Name: null,
            Description: '',
            shortlistType: '1',
          }}
          validationSchema={ShortlistAddCandidatesFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            handleChange,
            handleBlur,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {isSubmitting && <ProgressBar progress={progress} total={ids.length} />}

              {!isSubmitting && (
                <>
                  <div className="form__group form__group--compact">
                    <CheckboxOrRadioGroup
                      name="shortlistType"
                      type="radio"
                      optionsObjectList={ShortListTypeOptions}
                      selectedOptions={values.shortlistType}
                      directionRow={true}
                      controlFunc={e =>
                        onShortListTypeChange(e, {
                          handleChange,
                          setFieldValue,
                        })
                      }
                    />
                    {values.shortlistType === '0' ? (
                      <LazyLoadSelect
                        isSearchable={true}
                        name="Name"
                        value={values.Name}
                        isValid={(!errors.Name && !!touched.Name) || !touched.Name}
                        label="Shortlist"
                        required={true}
                        isMulti={false}
                        placeholder="Select From List"
                        getOption={getSharedShortlistOptions}
                        onChange={(name, value) =>
                          onChangeShortlist(name, value, { setFieldValue })
                        }
                        onBlur={handleBlur}
                        labelStyles={{
                          fontWeight: 'bold',
                          fontSize: '0.9286rem',
                        }}
                      />
                    ) : (
                      <CreatableLazySelect
                        name="Name"
                        value={values.Name || ''}
                        isValid={(!errors.Name && !!touched.Name) || !touched.Name}
                        label="Shortlist"
                        required={true}
                        isMulti={false}
                        placeholder="Create New Shortlist"
                        getOption={getShortlistOptions}
                        onChange={(name, value) =>
                          onChangeShortlist(name, value, { setFieldValue })
                        }
                        onBlur={handleBlur}
                        labelStyles={{
                          fontWeight: 'bold',
                          fontSize: '0.9286rem',
                        }}
                      />
                    )}
                    <ErrorMessage
                      name="Name"
                      render={message => (
                        <div className="form__error">{message}</div>
                      )}
                    />

                    <BasicLabel
                      name="Description"
                      required={false}
                      text="What Will This Shortlist Be Used For?"
                      styles={{
                        fontWeight: 'bold',
                        marginTop: '25px',
                      }}
                    />
                    <BasicTextArea
                      rows={6}
                      name="Description"
                      disabled={values.shortlistType === '0'}
                      content={values.Description || ''}
                      resize={false}
                      placeholder="0 characters of the 2000 character limit"
                      controlFunc={handleChange}
                      touchFunc={handleBlur}
                      isValid={
                        (!errors.Description && !!touched.Description) ||
                        !touched.Description
                      }
                    />
                    <ErrorMessage
                      name="Description"
                      render={message => (
                        <div className="form__error">{message}</div>
                      )}
                    />
                  </div>
                </>
              )}

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup
                  groupStyles={{ width: '100%', justifyContent: 'left' }}
                >
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                    disabled={isSubmitting}
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                    disabled={isSubmitting}
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

export default ShortlistAddCandidatesForm;
